<script setup lang="ts">
import { PhCaretRight } from '@phosphor-icons/vue';
import { nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import ContextMenuHeader from './ContextMenuHeader.vue';
import { copyNameToClipboard } from '../../logic/core/contextMenuBuilders';
import type {
  ContextMenuActionItem,
  ContextMenuSection,
} from '../../logic/core/useFileManagerContextMenu';

const props = defineProps<{
  position: { x: number; y: number };
  items: ContextMenuSection[];
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const menuRef = useTemplateRef<HTMLElement>('menu');
const menuStyle = ref({ top: `${props.position.y}px`, left: `${props.position.x}px` });

const selectItem = (item: ContextMenuActionItem) => {
  item.action();
  emit('close');
};

// Which submenu panel is open, as "{sectionIndex}:{itemIndex}" — one at a time by construction, and
// there is exactly one nesting level (ContextMenuSubmenuItem.items is typed to action items), so a
// single key is the whole state.
const openSubmenuKey = ref<string | null>(null);
const submenuKey = (sectionIndex: number, itemIndex: number) =>
  `${sectionIndex}:${itemIndex}`;

// A diagonal mouse path from the trigger row to its panel crosses sibling rows, which fires
// mouseleave on the wrapper. Closing on that instant would make the submenu almost unusable with a
// mouse, so leaving only SCHEDULES a close and re-entering cancels it.
const SUBMENU_CLOSE_GRACE_MS = 200;
let submenuCloseTimer: ReturnType<typeof setTimeout> | undefined;

const cancelSubmenuClose = () => {
  if (submenuCloseTimer !== undefined) {
    clearTimeout(submenuCloseTimer);
    submenuCloseTimer = undefined;
  }
};

const openSubmenu = (key: string) => {
  cancelSubmenuClose();
  openSubmenuKey.value = key;
};

const scheduleSubmenuClose = () => {
  cancelSubmenuClose();
  submenuCloseTimer = setTimeout(() => {
    openSubmenuKey.value = null;
    submenuCloseTimer = undefined;
  }, SUBMENU_CLOSE_GRACE_MS);
};

// Click-toggle alongside hover-open so the submenu is reachable on touch, where there is no hover at
// all. Deliberately no keyboard navigation: this menu has none today (Escape only), and adding arrow
// keys to just this one row would read as an accident rather than a feature.
const toggleSubmenu = (key: string) => {
  cancelSubmenuClose();
  openSubmenuKey.value = openSubmenuKey.value === key ? null : key;
};

// Closes on activation exactly as a menu item does — the header is an action now, not a caption, so
// leaving the menu open afterwards would read as "nothing happened" for something that is already
// silent by design.
const copyHeaderName = (name: string) => {
  copyNameToClipboard(name);
  emit('close');
};

const handleClickOutside = (event: MouseEvent) => {
  if (!menuRef.value?.contains(event.target as Node)) {
    emit('close');
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close');
  }
};

const handleScroll = () => {
  emit('close');
};

// Runs on every `position` change, not just once on mount: the menu component itself stays
// mounted across two right-clicks in a row (only `position`/`items` change, `isOpen` never
// toggles off in between), so relying on `onMounted` alone would leave the menu stuck at wherever
// it first appeared. Flips inward if it would overflow the viewport, so it never renders
// off-screen near an edge — measured after the position is applied, since it needs the
// component's own rendered size at that position.
// Whether the root menu was mirrored to the left of the click point. A submenu panel opens to the
// RIGHT of its trigger by default, which would send it straight back off the edge the root just
// flipped away from — so this mirrors the panel to the trigger's left in that case. Only the root's
// own flip is measured (see the accepted limitation in the plan); the panel is not re-measured.
const isFlippedHorizontally = ref(false);

const applyPosition = async (position: { x: number; y: number }) => {
  // A second right-click reuses this same mounted component, so an already-open panel from the
  // previous target must not survive into the new menu.
  cancelSubmenuClose();
  openSubmenuKey.value = null;
  menuStyle.value = { top: `${position.y}px`, left: `${position.x}px` };
  await nextTick();
  const el = menuRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  let { x: left, y: top } = position;
  if (rect.right > window.innerWidth) {
    left = Math.max(position.x - rect.width, 0);
  }
  if (rect.bottom > window.innerHeight) {
    top = Math.max(position.y - rect.height, 0);
  }
  isFlippedHorizontally.value = left !== position.x;
  menuStyle.value = { top: `${top}px`, left: `${left}px` };
};

watch(() => props.position, (position) => applyPosition(position), { immediate: true });

onMounted(() => {
  window.addEventListener('click', handleClickOutside);
  window.addEventListener('keydown', handleKeydown);
  // Capture phase: 'scroll' doesn't bubble, so a listener on window only sees scrolling of a
  // nested container (e.g. `.file-explorer-view-content`'s overflow-y: auto) during capture.
  window.addEventListener('scroll', handleScroll, true);
});

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside);
  window.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('scroll', handleScroll, true);
  // Alongside the listeners for the same reason: a pending close-grace timer would otherwise fire
  // against a torn-down component (selecting a submenu entry closes the menu inside the grace window
  // every single time).
  cancelSubmenuClose();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="context-menu" appear>
      <div ref="menu" class="context-menu" :style="menuStyle">
        <template v-for="(section, sectionIndex) in items" :key="sectionIndex">
          <hr v-if="sectionIndex > 0" class="context-menu-divider" />
          <template v-if="section.kind === 'header'">
            <ContextMenuHeader
              :icon="section.icon"
              :color="section.color"
              :name="section.name"
              @copy-name="copyHeaderName(section.name)"
            />
          </template>
          <template v-else>
            <template v-for="(entry, itemIndex) in section.items" :key="itemIndex">
              <!--
                The panel renders INSIDE this wrapper, not teleported: handleClickOutside closes on any
                click not contained by menuRef, so a teleported panel would have the very click that
                activates a tool close the menu before the action ran.

                It being a DOM descendant is also what makes the hover model work — moving from the
                trigger row into the panel never fires mouseleave here, so only a genuine detour
                through a sibling row starts the close grace.
              -->
              <div
                v-if="entry.kind === 'submenu'"
                class="context-menu-submenu"
                @mouseenter="openSubmenu(submenuKey(sectionIndex, itemIndex))"
                @mouseleave="scheduleSubmenuClose()"
              >
                <button
                  type="button"
                  class="context-menu-item context-menu-submenu-trigger"
                  :aria-expanded="openSubmenuKey === submenuKey(sectionIndex, itemIndex)"
                  @click="toggleSubmenu(submenuKey(sectionIndex, itemIndex))"
                >
                  <component :is="entry.icon" v-if="entry.icon" :size="16" />
                  <span>{{ entry.label }}</span>
                  <PhCaretRight :size="12" class="context-menu-submenu-caret" />
                </button>
                <div
                  v-if="openSubmenuKey === submenuKey(sectionIndex, itemIndex)"
                  class="context-menu-panel"
                  :class="{ 'context-menu-panel-flipped': isFlippedHorizontally }"
                >
                  <button
                    v-for="(child, childIndex) in entry.items"
                    :key="childIndex"
                    type="button"
                    class="context-menu-item"
                    :class="{ 'context-menu-item-danger': child.danger }"
                    @click="selectItem(child)"
                  >
                    <component :is="child.icon" v-if="child.icon" :size="16" />
                    <span>{{ child.label }}</span>
                  </button>
                </div>
              </div>
              <button
                v-else
                type="button"
                class="context-menu-item"
                :class="{ 'context-menu-item-danger': entry.danger }"
                @click="selectItem(entry)"
              >
                <component :is="entry.icon" v-if="entry.icon" :size="16" />
                <span>{{ entry.label }}</span>
              </button>
            </template>
          </template>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.context-menu {
  position: fixed;
  width: max-content;
  min-width: 8rem;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  transform-origin: top left;
}

/*
 * There is deliberately NO `overflow: hidden` here. It used to exist purely so the border-radius
 * clipped the first/last row's full-bleed hover background — but it also clipped an open submenu
 * panel (which sits outside this box, at left: 100% of its trigger) to zero width. The radius now
 * lives on the edge rows themselves, which solves the corners without confining the panel.
 *
 * Two selectors per edge because the last/first child may be the submenu WRAPPER div rather than a row
 * — the radius has to land on the row inside it, and setting it on the background-less wrapper too is
 * harmless. A child component's root (ContextMenuHeader's button) carries this component's scope
 * attribute, so `> :first-child` reaches it.
 */
.context-menu > :first-child,
.context-menu > :first-child > .context-menu-item {
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
}

.context-menu > :last-child,
.context-menu > :last-child > .context-menu-item {
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}

/* Only plays on the component actually mounting/unmounting (open/close) — repositioning while
   already open just updates `menuStyle` reactively, it doesn't remount this element, so moving
   the menu to a new right-click point stays instant rather than re-triggering this transition. */
.context-menu-enter-active,
.context-menu-leave-active {
  transition: opacity 0.12s ease-out, transform 0.12s ease-out;
}

.context-menu-enter-from,
.context-menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.context-menu-item {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  white-space: nowrap;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

.context-menu-item:hover {
  background-color: #f5f5f5;
}

/* Destructive entries (e.g. Delete) render in the app's shared danger color, text and icon alike —
   same red used app-wide via --text-danger (see assets/style/_variables.scss), so this reads
   consistently with other danger-styled UI rather than inventing a one-off hex value here. */
.context-menu-item-danger {
  color: var(--text-danger);
}

.context-menu-item-danger:hover {
  background-color: color-mix(in srgb, var(--text-danger) 8%, transparent);
}

.context-menu-divider {
  margin: 0.25rem 0;
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* Only exists to give the absolutely-positioned panel below a containing block. */
.context-menu-submenu {
  position: relative;
}

/*
 * The caret is pushed to the far edge rather than sitting beside the label, so a submenu row reads as
 * "there is more this way" instead of as a wider ordinary row.
 *
 * Deliberately `margin-left: auto` on the caret alone, NOT `justify-content: space-between` on the row:
 * the row has three children (icon, label, caret), so space-between spreads all three and strands the
 * label in the middle of the row — visibly out of line with the left-aligned labels of every ordinary
 * row above it. Only the caret should move.
 */
.context-menu-submenu-caret {
  flex: none;
  margin-left: auto;
  padding-left: 0.5rem;
  color: #6b6b6b;
}

/*
 * `top: 0` — flush with the trigger row's own top edge, so the panel's first row and the trigger row
 * share a top edge and, since both are `.context-menu-item` with identical padding, their labels sit on
 * one line. It previously overlapped upward by 0.25rem to widen the pointer's path into the first child
 * row; that is unnecessary (the panel is a DOM descendant of the wrapper, so moving into it never fires
 * the wrapper's mouseleave at all) and it made the first tool read as nudged up by the divider's margin,
 * which is the same 0.25rem.
 *
 * `overflow: hidden` is safe here in a way it is not on the root: ContextMenuSubmenuItem.items is
 * typed to action items, so there can never be a grandchild panel for this to clip.
 */
.context-menu-panel {
  position: absolute;
  top: 0;
  left: 100%;
  width: max-content;
  min-width: 8rem;
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

/* Mirrored when the root menu itself was flipped inward from the right edge — opening rightward there
   would put the panel straight back off-screen. */
.context-menu-panel-flipped {
  left: auto;
  right: 100%;
}
</style>
